import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("pr_token")}` });

function Field({ label, value, onChange, testid }) {
  return (
    <label className="text-xs text-slate-600 space-y-1">
      <span className="block font-medium">{label}</span>
      <Input type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-40" data-testid={testid} />
    </label>
  );
}

function LogTable({ rows }) {
  if (!rows.length) return <p className="text-xs text-slate-500" data-testid="bop-log-empty">No BOP PRs evaluated yet.</p>;
  return (
    <table className="w-full text-xs" data-testid="bop-log-table">
      <thead className="text-left text-slate-500 uppercase text-[10px]">
        <tr><th className="py-1">PR</th><th>Type</th><th>Supplier</th><th>Total ₹</th><th>AI</th><th>Result</th><th>Why not</th><th>When</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-100 align-top" data-testid={`bop-log-row-${r.voc_no}`}>
            <td className="py-1 font-mono">#{r.voc_no}</td>
            <td data-testid={`bop-log-type-${r.voc_no}`}>{r.purc_type || "BOP"}</td>
            <td className="font-mono">{r.pcode}</td>
            <td>{r.total_inr != null ? Math.round(r.total_inr).toLocaleString("en-IN") : "—"}</td>
            <td className={r.ai_action === "HOLD" ? "text-rose-700 font-semibold" : r.ai_action === "REVIEW" ? "text-amber-700" : "text-emerald-700"}>{r.ai_action || "—"}</td>
            <td className={r.result === "auto_approved" ? "text-emerald-700 font-semibold" : "text-slate-500"}>
              {r.result === "auto_approved" ? "Auto-approved" : "Normal flow"}
            </td>
            <td className="text-slate-600 max-w-md">{(r.reasons || []).join("; ") || "—"}</td>
            <td className="whitespace-nowrap text-slate-500">{new Date(r.at).toLocaleString("en-IN")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function BopAutoApproveCard() {
  const [s, setS] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API}/admin/bop-auto-approve/settings`, { headers: headers() }),
      axios.get(`${API}/admin/bop-auto-approve/log`, { headers: headers() }),
    ]);
    setS(a.data); setRows(b.data || []);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load BOP auto-approve settings")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled: s.enabled, limit_inr: Number(s.limit_inr), consume_enabled: s.consume_enabled };
      const r = await axios.put(`${API}/admin/bop-auto-approve/settings`, body, { headers: headers() });
      setS(r.data); toast.success("BOP auto-approve settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!s) return <div className="bg-white border border-slate-200 rounded-sm p-4"><Loader2 className="animate-spin text-slate-400" /></div>;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4" data-testid="bop-auto-approve-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-slate-800 inline-flex items-center gap-2"><BadgeCheck size={16} className="text-cyan-700" /> BOP &amp; Consume list-price auto-approve</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            A new BOP PR is auto-approved (Requestor, L1, L2) when every line is a BOM item, priced exactly at a Released SAP list price that was created through Smart PR,
            and the PR total is below the limit. A Consume PR is auto-approved when every line's ERP code is exactly a SAP item code that is Active at the PR's site,
            priced exactly at a Released Smart PR list price, under the same limit. The AI suggestion (even HOLD) does not block; it is noted in the approval trail. Requestor, L1 and L2 get an FYI email.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={s.enabled} onCheckedChange={set("enabled")} data-testid="bop-enabled-switch" />
          <span className={`text-sm font-semibold ${s.enabled ? "text-emerald-700" : "text-slate-500"}`} data-testid="bop-enabled-label">{s.enabled ? "On" : "Off"}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <Field label="PR total must be below (₹)" value={s.limit_inr} onChange={set("limit_inr")} testid="bop-limit-input" />
        <label className="flex items-center gap-2 text-xs text-slate-600 h-8">
          <Switch checked={s.consume_enabled !== false} onCheckedChange={set("consume_enabled")} data-testid="consume-enabled-switch" />
          Include Consume PRs
        </label>
        <Button onClick={save} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800 h-8" data-testid="bop-save-btn">
          {saving && <Loader2 size={14} className="animate-spin mr-1" />} Save
        </Button>
      </div>
      <div>
        <h3 className="text-xs font-semibold text-slate-700 mb-1">Recent BOP &amp; Consume PRs</h3>
        <LogTable rows={rows} />
      </div>
    </div>
  );
}
